import {Banner} from '@primer/react'

import type {PageLimits} from '../page-data/payloads/files'

/**
 * Props for the PageLimitsBanner component
 */
export type PageLimitsBannerProps = {
  /**
   * Object containing information about page limits and whether they are exceeded
   * Includes limits for files, review threads, and annotations
   */
  pageLimits: PageLimits
}

// Helper function to determine if thread pagination is enabled
// Can be removed once thread_preview_pagination and pr_files_limit_max_check FFs are graduated
export function isThreadPaginationEnabled(pageLimits: PageLimits): boolean {
  return pageLimits.reviewThreadsPaginatedLimit > pageLimits.reviewThreadsLimit
}

/**
 * Displays a warning banner when page limits are exceeded in a pull request.
 *
 * Shows a banner when files,
 *  review threads, or annotations limits are exceeded as defined in pageLimits
 *
 * The banner includes a link to switch back to the classic pull request page
 * to view all content without limits.
 */
export function PageLimitsBanner({pageLimits}: PageLimitsBannerProps) {
  const pageLimitsExceeded =
    pageLimits.filesLimitExceeded || pageLimits.reviewThreadsLimitExceeded || pageLimits.annotationsLimitExceeded

  const limitExceededMessage = () => {
    const messagePrefix = 'Only the '
    const messagePostfix = ' are currently being shown.'

    const exceededLimitCounts = []
    if (pageLimits.filesLimitExceeded) {
      exceededLimitCounts.push(`first ${pageLimits.filesLimit} files`)
    }
    if (pageLimits.reviewThreadsLimitExceeded) {
      exceededLimitCounts.push(`latest ${pageLimits.reviewThreadsLimit} comments`)
    }
    if (pageLimits.annotationsLimitExceeded) {
      exceededLimitCounts.push(`first ${pageLimits.annotationsLimit} alerts`)
    }

    let formattedLimits = ''
    if (exceededLimitCounts.length === 1) {
      formattedLimits = `${exceededLimitCounts[0]}`
    } else if (exceededLimitCounts.length === 2) {
      formattedLimits = `${exceededLimitCounts[0]} and ${exceededLimitCounts[1]}`
    } else if (exceededLimitCounts.length === 3) {
      formattedLimits = `${exceededLimitCounts[0]}, ${exceededLimitCounts[1]}, and ${exceededLimitCounts[2]}`
    }

    let message = messagePrefix + formattedLimits + messagePostfix

    // Add hint about Comments side panel when review threads limit is exceeded
    // and the pagination limit allows viewing more comments than the diff limit
    if (pageLimits.reviewThreadsLimitExceeded && isThreadPaginationEnabled(pageLimits)) {
      if (pageLimits.reviewThreadsPaginatedLimitExceeded) {
        message += ` To view up to ${pageLimits.reviewThreadsPaginatedLimit} comments, open the comments side panel.`
      } else {
        message += ' To view the remaining comments, open the comments side panel'
      }
    }

    return message
  }

  const description = <span>{limitExceededMessage()}</span>

  if (!pageLimitsExceeded) return null

  return (
    <Banner
      aria-label="Warning"
      title="Warning"
      variant="warning"
      hideTitle
      description={description}
      className="tmp-mb-3"
    />
  )
}
